import { SectionType, Structure } from '@wix/add-panel-common';
import React, { FC, useCallback } from 'react';
import { useActionsDispatcher } from '../../../../ActionsDispatcher';
import { useCurrentCategory } from '../../../../CurrentCategoryProvider';
import { useUtils } from '../../../../UtilsProvider';
import { SectionViewParentType } from '../../../../UtilsProvider/UtilsProvider.types';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import styles from './ThemedSection.scss';
import type { ThemedSectionProps } from './ThemedSection.types';

export const ThemedSection: FC<ThemedSectionProps> = ({
  section,
  parentType = SectionViewParentType.AddPanel,
  selectedItemKey,
  checkItemSelected = () => false,
}) => {
  const category = useCurrentCategory();
  const { sectionName, type } = section;
  const { ThemedPreview, PersonalizedPreview } = useUtils();

  const { dispatchComponentClick, dispatchComponentMouseEnter, dispatchComponentMouseLeave } = useActionsDispatcher();

  const handleMouseEnter = (compDef: Structure, itemId: string) => {
    dispatchComponentMouseEnter({ compDef, itemId, metaData: { sectionName, categoryId: category.id } });
  };
  const handleMouseLeave = (itemId: string) => {
    dispatchComponentMouseLeave({ itemId });
  };

  const handleClick = useCallback(
    (structure, _sectionName, tags, itemId) => {
      dispatchComponentClick({ itemId, category, compDef: structure, section });
    },
    [dispatchComponentClick, category, section],
  );

  const { startItemDrag } = useDragAndDrop();

  const handleItemDrag = useCallback(
    (event, compData) => {
      startItemDrag(event, {
        categoryId: compData.categoryId,
        section,
        itemId: compData.structure.componentType,
        itemStructure: compData.structure,
        rect: compData.rect,
      });
    },
    [section, startItemDrag],
  );

  const PreviewComponent =
    type === SectionType.Theme ? ThemedPreview : type === SectionType.Personalized ? PersonalizedPreview : null;

  if (!PreviewComponent) {
    return null;
  }

  return (
    <PreviewComponent
      {...section}
      title={section.translations.title}
      categoryId={category.id}
      className={styles.preview}
      parentType={parentType}
      showSectionHeader={false}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onItemDrag={handleItemDrag}
      selectedItemKey={selectedItemKey}
      isSelectedItem={checkItemSelected}
    />
  );
};
